import { useEffect } from 'react';
import { saveUserInfo, klaviyoIdentify } from '@/services/forms.service';
import { trackEvent, trackEventForGA4, trackEventDirect } from '@/utils/gaDataPush';
import {
  isMobileDevice,
  saveChatOriginToStorage,
  getChatOriginFromStorage,
  removeChatOriginFromStorage,
} from '@/utils/utils';
import useStore from '@/store/useStore';

let trackedStartChat = false;

const { setChatOrigin } = useStore.getState();

export function openDEChat(origin = '') {
  const liveChatTips = document.querySelector(`#live-chat-tips-desktop`)!;

  const storedOrigin = getChatOriginFromStorage();
  const calculatedChatOrigin = storedOrigin || origin || 'other';

  const clientWindow = window as any;

  if (clientWindow && clientWindow.embeddedservice_bootstrap && clientWindow.embeddedservice_bootstrap.utilAPI) {
    (window as any).embeddedservice_bootstrap.utilAPI.launchChat().then(() => {
      liveChatTips?.classList?.remove('active');
      trackEventForGA4('chat_now_click', { chat_origin: calculatedChatOrigin });
    });
  }
}

function togglePopupChatTips(state?: 'open' | 'close') {
  const chatTipElement = document.querySelector('#live-chat-tips-desktop');
  const origin = getChatOriginFromStorage();
  if (chatTipElement) {
    if (state === 'open') {
      chatTipElement.classList.add('active');
      if (origin !== 'general') {
        setChatOrigin('disruptive');
      }
    } else if (state === 'close') {
      chatTipElement.classList.remove('active');
    } else {
      chatTipElement.classList.toggle('active');
      if (chatTipElement.classList.contains('active')) {
        setChatOrigin('disruptive');
      } else {
        setChatOrigin('general');
      }
    }
  }
}

function liveChatCloseGA() {
  trackEventDirect('Engagement', 'LiveChat', 'Chat|Close', true);
  trackEventForGA4('live_chat_close', { chat_origin: useStore.getState().chatOrigin });
}

function liveChatMinGA() {
  trackEventDirect('Engagement', 'LiveChat', 'Chat|Minimize', true);
  trackEventForGA4('live_chat_minimize', { chat_origin: useStore.getState().chatOrigin });
}

async function liveChatStateGA(data, e) {
  const { track_event: trackGAEvent } = window as any;
  const origin = useStore.getState().chatOrigin;

  if (data === 'end') {
    trackGAEvent('Engagement', 'LiveChat', 'Chat|End', false);
    trackEventForGA4('live_chat_end', { chat_origin: origin });
    removeChatOriginFromStorage();
  } else if (data === 'waiting') {
    await pushUserInfo(e);
    trackGAEvent('Engagement', 'LiveChat', 'Chat|Submit', false);
    trackEventForGA4('live_chat_submit', { chat_origin: origin });
  } else if (data === 'chatting' && !trackedStartChat) {
    trackGAEvent('Engagement', 'LiveChat', 'Chat|Start Chat', false);
    trackEventForGA4('liveperson_chat', { chat_origin: origin });
    trackedStartChat = true;
    trackEventForGA4('live_chat_start', { chat_origin: origin });
  } else if (data === 'interactive') {
    trackGAEvent('Engagement', 'LiveChat', 'Chat|Interaction', false);
    trackEventForGA4('live_chat_interaction', { chat_origin: origin });
  }
}

async function pushUserInfo(event) {
  const lpName = event.data.data['What_is_your_name'];
  const lpEmail = event.data.data['_email'];
  if (lpName && lpEmail) {
    const nameParts = String(lpName).trim().split(/\s+/);
    klaviyoIdentify({
      email: lpEmail,
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
    });
    const response = await saveUserInfo(lpName, lpEmail);
    const userInfo = response.data.user_info;
    if (userInfo) {
      (window as any).dataLayer.push(userInfo);
    }
  }
}

export function liveChatGAInit() {
  (window as any).addEventListener(
    'message',
    e => {
      if (e.origin === (window as any).SALESFORCE_ORIGIN) {
        if (e.data['method'] === 'ESW_APP_MINIMIZE') {
          liveChatMinGA();
        } else if (e.data['method'] === 'ESW_APP_RESET_INITIAL_STATE') {
          liveChatCloseGA();
        } else if (e.data['method'] === 'ESW_APP_PRECHAT_SUBMIT') {
          liveChatStateGA('waiting', e);
        } else if (e.data['method'] === 'ESW_APP_SHOW_MINIMIZED_STATE_NOTIFICATION') {
          liveChatStateGA('chatting', e);
        } else if (e.data['method'] === 'ESW_CLEAN_UP_WEBSTORAGE_FAILEDMESSAGES_EVENT') {
          liveChatStateGA('end', e);
        }
        // interactive
        else if (e.data['method'] === 'EMBEDDED_MESSAGING_CONVERSATION_ID_UPDATE') {
          liveChatStateGA('interactive', e);
        }
      }
    },
    false
  );
  setTimeout(() => togglePopupChatTips('open'), isMobileDevice() ? 30000 : 10000);
}

export default function DigitalEngagementChat() {
  const ChatOriginFromStore = useStore(state => state.chatOrigin);

  useEffect(() => {
    const handleChatClick = (e: MouseEvent, triggered?: boolean) => {
      if ((e.target as HTMLElement).closest('.embedded-messaging') && triggered === undefined) {
        const chatTipElement = document.querySelector('#live-chat-tips-desktop');
        const isDisruptive = chatTipElement?.classList.contains('active');

        const origin = getChatOriginFromStorage();
        const calculatedChatOrigin = origin || (isDisruptive ? 'disruptive' : 'general');
        setChatOrigin(calculatedChatOrigin);

        if (isDisruptive) {
          trackEventDirect('Pop Up Window', 'Select', 'LiveChat:Window', true);
          trackEventForGA4('chat_now_click', {
            chat_origin: calculatedChatOrigin,
          });
        } else {
          trackEventDirect('Pop Up Window', 'Select', 'LiveChat:ChatButton', true);
          trackEventForGA4('live_chat_open', { chat_origin: 'general' });
        }

        const typedWindow = window as any;

        const isTouchable = Boolean('ontouchstart' in typedWindow);

        typedWindow.dataLayer.push({
          event: 'trackEvent',
          eventCategory: 'Live Chat',
          eventAction: 'Bubble',
          eventLabel: isMobileDevice() || isTouchable ? 'Mobile' : 'Desktop',
          eventInteraction: undefined,
        });

        chatTipElement?.classList.remove('active');
      }
    };

    document.body.addEventListener('click', handleChatClick);

    return () => {
      document.body.removeEventListener('click', handleChatClick);
    };
  }, [ChatOriginFromStore]);

  useEffect(() => {
    const storedChatOrigin = getChatOriginFromStorage();
    if (storedChatOrigin) {
      setChatOrigin(storedChatOrigin);
    }
  }, []);

  useEffect(() => {
    if (ChatOriginFromStore) {
      saveChatOriginToStorage(ChatOriginFromStore);
    }

    return () => {
      removeChatOriginFromStorage();
    };
  }, [ChatOriginFromStore]);

  return (
    <>
      <div id="live-chat-tips-desktop">
        <i
          className="iconfont iconfont-close1 google-event-tracker"
          data-category="Pop Up Window"
          data-action="Select"
          data-label="LiveChat:Close"
          onClick={e => {
            togglePopupChatTips('close');
            trackEvent(e as any);
          }}
          aria-label="Close"
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              togglePopupChatTips('close');
              trackEvent(e as any);
            }
          }}
        />
        <div
          className="ir261_settingpdp_livechat_popup"
          data-category="Pop Up Window"
          data-action="Select"
          data-label="LiveChat:Window"
          data-noninteract="true"
          onClick={() => {
            openDEChat('disruptive');
          }}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              openDEChat('disruptive');
            }
          }}
        >
          <h4>Have a question?</h4>
          <p>We&apos;re happy to help</p>
        </div>
      </div>
      <div
        id="sfde-chat-wrapper-10"
        data-event1-action="Select"
        data-event1-category="Pop Up Window"
        data-event1-label="LiveChat:ChatButton"
        data-noninteract="false"
        className="live_chat_bubble_desktop_position"
      />
    </>
  );
}
